import Seo from "../components/Seo";
import { SEO_ROUTE_PROPS } from "../seo/metadata";

type ChangelogEntry = {
	/** ISO date, used for the <time> element and as a stable key. */
	date: string;
	/** Human-friendly date shown in the left rail. */
	dateLabel: string;
	title: string;
	summary?: string;
	items: string[];
};

// Newest first. To publish an update, add a new entry at the top of this list.
const CHANGELOG_ENTRIES: ChangelogEntry[] = [
	{
		date: "2026-07-11",
		dateLabel: "July 11, 2026",
		title: "Pull I-94s and travel history, and auto-file mailed notices",
		summary:
			"Two time savers for busy caseloads. Retrieve a client's official travel records on demand, and let Clerical file the paper notices that arrive in your mailbox.",
		items: [
			"Retrieve a client's most recent I-94 and full travel history from CBP, returned as official PDFs ready for the file.",
			"Scan a government notice on your office copier and Clerical identifies it and files it to the right matter automatically.",
		],
	},
	{
		date: "2026-07-08",
		dateLabel: "July 8, 2026",
		title: "Invite your team to Clerical",
		summary:
			"Clerical is better with your whole firm. Add colleagues to your workspace and manage everyone in one place.",
		items: [
			"Invite team members by email and manage who has access.",
			"Simple per-seat billing that grows with your firm.",
		],
	},
	{
		date: "2026-06-24",
		dateLabel: "June 24, 2026",
		title: "Smarter documents and a deeper Clio connection",
		summary:
			"A round of upgrades to how Clerical reads, drafts, and organizes your firm's work.",
		items: [
			"Read and summarize long PDFs and case documents in seconds.",
			"Draft cover letters and correspondence on your firm's letterhead.",
			"Find matters in Clio by practice area and status, and check Clio calendars.",
			"Drafted emails are saved automatically to the matter in Clio.",
			"Look up immigration court (EOIR) case status by A-number.",
			"Jump back to any past conversation with quick search.",
		],
	},
	{
		date: "2026-06-19",
		dateLabel: "June 19, 2026",
		title: "Assisted petition preparation, with you in control",
		summary:
			"Clerical can now help assemble entire petition packages step by step, starting with family-based I-130 petitions and I-765 work-authorization applications. It pauses for review at each checkpoint, so nothing moves forward until an attorney approves.",
		items: [
			"Guided, start-to-finish preparation for common filings (I-130, I-765).",
			"Built-in review checkpoints keep an attorney in control at every step.",
			"Clerical drafts the forms and supporting materials. Your team reviews and approves.",
		],
	},
	{
		date: "2026-05-23",
		dateLabel: "May 23, 2026",
		title: "Immigration research, built in",
		summary:
			"Ask Clerical the questions that used to mean digging through government websites. It checks official sources and brings back the answer with the details you need.",
		items: [
			"USCIS case status and current processing times.",
			"Visa Bulletin priority dates and consular appointment wait times.",
			"USCIS filing-fee calculator.",
			"Consular case status (CEAC), visa reciprocity, and civil-document requirements by country.",
		],
	},
	{
		date: "2026-05-06",
		dateLabel: "May 6, 2026",
		title: "Clerical for Outlook",
		summary:
			"Clerical now works right inside Outlook. Open a client email and Clerical can draft a reply in your voice, pull the relevant case details, and prepare documents without ever leaving your inbox.",
		items: [
			"Draft and reply to client emails with your own signature and tone.",
			"Prepare and edit Word, PDF, and Excel documents from an email thread.",
			"Assemble USCIS filing packets by filling forms, combining PDFs, and capturing web pages as exhibits.",
			"Prefer a full workspace? A dedicated Clerical chat is available on the web.",
		],
	},
	{
		date: "2026-04-23",
		dateLabel: "April 23, 2026",
		title: "Introducing Clerical",
		summary:
			"Clerical is an AI assistant built for immigration law firms. It handles the administrative side of casework alongside your team, drafting emails, organizing documents, and keeping matters moving. That frees your attorneys and paralegals to focus on clients.",
		items: [
			"Connect the tools you already use, including Microsoft Outlook, Microsoft Teams, and Clio.",
			"Browse and review your Clio matters and client files in one place.",
			"A firm-wide assistant your whole team can put to work from day one.",
		],
	},
];

export default function Changelog() {
	return (
		<div
			ref={(el) => el?.scrollIntoView()}
			className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14"
		>
			<Seo {...SEO_ROUTE_PROPS["/changelog"]} />
			<header className="border-b pb-8">
				<h1 className="text-4xl font-semibold tracking-tight">Changelog</h1>
				<p className="mt-3 leading-7 text-muted-foreground">
					New features and product updates from the Clerical team.
				</p>
			</header>

			<div className="mt-10 space-y-12 sm:mt-14 sm:space-y-14">
				{CHANGELOG_ENTRIES.map((entry) => (
					<article
						key={entry.date}
						className="grid gap-x-10 gap-y-4 md:grid-cols-[9rem_1fr]"
					>
						<div className="md:pt-1">
							<time
								dateTime={entry.date}
								className="text-sm font-medium text-muted-foreground"
							>
								{entry.dateLabel}
							</time>
						</div>
						<div className="space-y-4">
							<h2 className="text-2xl font-semibold tracking-tight">
								{entry.title}
							</h2>
							{entry.summary && (
								<p className="leading-7 text-foreground/90">{entry.summary}</p>
							)}
							<ul className="list-disc space-y-2 pl-5 leading-7 marker:text-muted-foreground">
								{entry.items.map((item) => (
									<li key={`${entry.date}-${item}`}>{item}</li>
								))}
							</ul>
						</div>
					</article>
				))}
			</div>
		</div>
	);
}
